import {
  LegacyPortalDashboardAgeGroup,
  LegacyPortalDashboardGender,
  LegacyPortalDashboardPrefecture,
} from "@spwn-portal/grpc";

import type { GetUserAttributeCountsResponse } from "./useGetUserAttributeCounts";

type GenderChartKey = "female" | "male" | "other" | "noAnswer";

type AgeGroupChartRow = {
  ageGroup: LegacyPortalDashboardAgeGroup;
  label: string;
  total: number;
  female: number;
  male: number;
  other: number;
  noAnswer: number;
};

type GenderChartRow = {
  gender: LegacyPortalDashboardGender;
  label: string;
  color: string;
  count: number;
};

type PrefectureChartRow = {
  prefecture: LegacyPortalDashboardPrefecture;
  label: string;
  count: number;
  isNoAnswer: boolean;
};

type UserAttributeDashboardData = {
  ageGroupChartData: AgeGroupChartRow[];
  genderChartData: GenderChartRow[];
  prefectureChartData: PrefectureChartRow[];
};

export const genderChartSeries: {
  gender: LegacyPortalDashboardGender;
  key: GenderChartKey;
  label: string;
  color: string;
}[] = [
  {
    gender: LegacyPortalDashboardGender.FEMALE,
    key: "female",
    label: "女性",
    color: "#00c2ae",
  },
  {
    gender: LegacyPortalDashboardGender.MALE,
    key: "male",
    label: "男性",
    color: "#5aa9c9",
  },
  {
    gender: LegacyPortalDashboardGender.OTHER,
    key: "other",
    label: "その他",
    color: "#9083c9",
  },
  {
    gender: LegacyPortalDashboardGender.NO_ANSWER,
    key: "noAnswer",
    label: "未回答",
    color: "#8b96a2",
  },
];

const ageGroupOrder = [
  LegacyPortalDashboardAgeGroup.UNDER_10,
  LegacyPortalDashboardAgeGroup.FROM_10_TO_19,
  LegacyPortalDashboardAgeGroup.FROM_20_TO_29,
  LegacyPortalDashboardAgeGroup.FROM_30_TO_39,
  LegacyPortalDashboardAgeGroup.FROM_40_TO_49,
  LegacyPortalDashboardAgeGroup.FROM_50_TO_59,
  LegacyPortalDashboardAgeGroup.FROM_60_TO_69,
  LegacyPortalDashboardAgeGroup.OVER_70,
  LegacyPortalDashboardAgeGroup.NO_ANSWER,
];

const ageGroupLabels: Record<LegacyPortalDashboardAgeGroup, string> = {
  [LegacyPortalDashboardAgeGroup.UNSPECIFIED]: "未回答",
  [LegacyPortalDashboardAgeGroup.UNDER_10]: "10歳未満",
  [LegacyPortalDashboardAgeGroup.FROM_10_TO_19]: "10代",
  [LegacyPortalDashboardAgeGroup.FROM_20_TO_29]: "20代",
  [LegacyPortalDashboardAgeGroup.FROM_30_TO_39]: "30代",
  [LegacyPortalDashboardAgeGroup.FROM_40_TO_49]: "40代",
  [LegacyPortalDashboardAgeGroup.FROM_50_TO_59]: "50代",
  [LegacyPortalDashboardAgeGroup.FROM_60_TO_69]: "60代",
  [LegacyPortalDashboardAgeGroup.OVER_70]: "70代以上",
  [LegacyPortalDashboardAgeGroup.NO_ANSWER]: "未回答",
};

const prefectureLabels: Record<LegacyPortalDashboardPrefecture, string> = {
  [LegacyPortalDashboardPrefecture.UNSPECIFIED]: "未回答",
  [LegacyPortalDashboardPrefecture.HOKKAIDO]: "北海道",
  [LegacyPortalDashboardPrefecture.AOMORI]: "青森県",
  [LegacyPortalDashboardPrefecture.IWATE]: "岩手県",
  [LegacyPortalDashboardPrefecture.MIYAGI]: "宮城県",
  [LegacyPortalDashboardPrefecture.AKITA]: "秋田県",
  [LegacyPortalDashboardPrefecture.YAMAGATA]: "山形県",
  [LegacyPortalDashboardPrefecture.FUKUSHIMA]: "福島県",
  [LegacyPortalDashboardPrefecture.IBARAKI]: "茨城県",
  [LegacyPortalDashboardPrefecture.TOCHIGI]: "栃木県",
  [LegacyPortalDashboardPrefecture.GUNMA]: "群馬県",
  [LegacyPortalDashboardPrefecture.SAITAMA]: "埼玉県",
  [LegacyPortalDashboardPrefecture.CHIBA]: "千葉県",
  [LegacyPortalDashboardPrefecture.TOKYO]: "東京都",
  [LegacyPortalDashboardPrefecture.KANAGAWA]: "神奈川県",
  [LegacyPortalDashboardPrefecture.NIIGATA]: "新潟県",
  [LegacyPortalDashboardPrefecture.TOYAMA]: "富山県",
  [LegacyPortalDashboardPrefecture.ISHIKAWA]: "石川県",
  [LegacyPortalDashboardPrefecture.FUKUI]: "福井県",
  [LegacyPortalDashboardPrefecture.YAMANASHI]: "山梨県",
  [LegacyPortalDashboardPrefecture.NAGANO]: "長野県",
  [LegacyPortalDashboardPrefecture.GIFU]: "岐阜県",
  [LegacyPortalDashboardPrefecture.SHIZUOKA]: "静岡県",
  [LegacyPortalDashboardPrefecture.AICHI]: "愛知県",
  [LegacyPortalDashboardPrefecture.MIE]: "三重県",
  [LegacyPortalDashboardPrefecture.SHIGA]: "滋賀県",
  [LegacyPortalDashboardPrefecture.KYOTO]: "京都府",
  [LegacyPortalDashboardPrefecture.OSAKA]: "大阪府",
  [LegacyPortalDashboardPrefecture.HYOGO]: "兵庫県",
  [LegacyPortalDashboardPrefecture.NARA]: "奈良県",
  [LegacyPortalDashboardPrefecture.WAKAYAMA]: "和歌山県",
  [LegacyPortalDashboardPrefecture.TOTTORI]: "鳥取県",
  [LegacyPortalDashboardPrefecture.SHIMANE]: "島根県",
  [LegacyPortalDashboardPrefecture.OKAYAMA]: "岡山県",
  [LegacyPortalDashboardPrefecture.HIROSHIMA]: "広島県",
  [LegacyPortalDashboardPrefecture.YAMAGUCHI]: "山口県",
  [LegacyPortalDashboardPrefecture.TOKUSHIMA]: "徳島県",
  [LegacyPortalDashboardPrefecture.KAGAWA]: "香川県",
  [LegacyPortalDashboardPrefecture.EHIME]: "愛媛県",
  [LegacyPortalDashboardPrefecture.KOCHI]: "高知県",
  [LegacyPortalDashboardPrefecture.FUKUOKA]: "福岡県",
  [LegacyPortalDashboardPrefecture.SAGA]: "佐賀県",
  [LegacyPortalDashboardPrefecture.NAGASAKI]: "長崎県",
  [LegacyPortalDashboardPrefecture.KUMAMOTO]: "熊本県",
  [LegacyPortalDashboardPrefecture.OITA]: "大分県",
  [LegacyPortalDashboardPrefecture.MIYAZAKI]: "宮崎県",
  [LegacyPortalDashboardPrefecture.KAGOSHIMA]: "鹿児島県",
  [LegacyPortalDashboardPrefecture.OKINAWA]: "沖縄県",
  [LegacyPortalDashboardPrefecture.NO_ANSWER]: "未回答",
};

export const createUserAttributeDashboardData = (
  response: GetUserAttributeCountsResponse
): UserAttributeDashboardData => ({
  ageGroupChartData: createAgeGroupChartData(response.ageGroupGenderCounts),
  genderChartData: createGenderChartData(response.genderCounts),
  prefectureChartData: createPrefectureChartData(response.prefectureCounts),
});

const createAgeGroupChartData = (
  ageGroupGenderCounts: GetUserAttributeCountsResponse["ageGroupGenderCounts"]
): AgeGroupChartRow[] => {
  const rows = new Map(
    ageGroupOrder.map((ageGroup) => [
      ageGroup,
      createEmptyAgeGroupChartRow(ageGroup),
    ])
  );

  ageGroupGenderCounts.forEach(({ ageGroup, gender, count }) => {
    const normalizedAgeGroup =
      ageGroup === LegacyPortalDashboardAgeGroup.UNSPECIFIED
        ? LegacyPortalDashboardAgeGroup.NO_ANSWER
        : ageGroup;
    const row =
      rows.get(normalizedAgeGroup) ??
      rows.get(LegacyPortalDashboardAgeGroup.NO_ANSWER);

    if (!row) {
      return;
    }

    const genderKey = getGenderChartKey(gender);
    row[genderKey] += count;
    row.total += count;
  });

  return ageGroupOrder.map(
    (ageGroup) => rows.get(ageGroup) ?? createEmptyAgeGroupChartRow(ageGroup)
  );
};

const createEmptyAgeGroupChartRow = (
  ageGroup: LegacyPortalDashboardAgeGroup
): AgeGroupChartRow => ({
  ageGroup,
  label: ageGroupLabels[ageGroup],
  total: 0,
  female: 0,
  male: 0,
  other: 0,
  noAnswer: 0,
});

const createGenderChartData = (
  genderCounts: GetUserAttributeCountsResponse["genderCounts"]
): GenderChartRow[] => {
  const counts = new Map<LegacyPortalDashboardGender, number>();

  genderCounts.forEach(({ gender, count }) => {
    const normalizedGender =
      gender === LegacyPortalDashboardGender.UNSPECIFIED
        ? LegacyPortalDashboardGender.NO_ANSWER
        : gender;
    counts.set(normalizedGender, (counts.get(normalizedGender) ?? 0) + count);
  });

  return genderChartSeries.map(({ gender, label, color }) => ({
    gender,
    label,
    color,
    count: counts.get(gender) ?? 0,
  }));
};

const createPrefectureChartData = (
  prefectureCounts: GetUserAttributeCountsResponse["prefectureCounts"]
): PrefectureChartRow[] => {
  const counts = new Map<LegacyPortalDashboardPrefecture, number>();

  prefectureCounts.forEach(({ prefecture, count }) => {
    const normalizedPrefecture =
      prefecture === LegacyPortalDashboardPrefecture.UNSPECIFIED
        ? LegacyPortalDashboardPrefecture.NO_ANSWER
        : prefecture;
    counts.set(
      normalizedPrefecture,
      (counts.get(normalizedPrefecture) ?? 0) + count
    );
  });

  return Array.from(counts.entries())
    .map(([prefecture, count]) => ({
      prefecture,
      label: prefectureLabels[prefecture] ?? "未回答",
      count,
      isNoAnswer: prefecture === LegacyPortalDashboardPrefecture.NO_ANSWER,
    }))
    .sort((a, b) => {
      if (a.isNoAnswer !== b.isNoAnswer) {
        return a.isNoAnswer ? 1 : -1;
      }

      return b.count - a.count;
    });
};

const getGenderChartKey = (
  gender: LegacyPortalDashboardGender
): GenderChartKey => {
  switch (gender) {
    case LegacyPortalDashboardGender.FEMALE:
      return "female";
    case LegacyPortalDashboardGender.MALE:
      return "male";
    case LegacyPortalDashboardGender.OTHER:
      return "other";
    case LegacyPortalDashboardGender.UNSPECIFIED:
    case LegacyPortalDashboardGender.NO_ANSWER:
    default:
      return "noAnswer";
  }
};
